import { Link, createFileRoute, notFound } from "@tanstack/react-router";

import { CihClosingCta } from "~/components/cih/closing-cta";
import { CihCoachCellGrid } from "~/components/cih/coach-cell";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import {
  getAllCcoCoachesFn,
  getPublishedCcoWithRelationsFn,
  type CcoWithRelations,
} from "~/lib/functions/cih/ccos";
import { createBrandMeta } from "~/lib/shared/meta";

type LoaderData = {
  title: string;
  cco: CcoWithRelations["cco"];
  coaches: CcoWithRelations["coaches"];
};

export const Route = createFileRoute("/_public/organizations/$slug/coaches")({
  staticData: { title: "Coaches" },
  loader: async ({ params }): Promise<LoaderData> => {
    const [parent, coaches] = await Promise.all([
      getPublishedCcoWithRelationsFn({ data: { slug: params.slug } }),
      getAllCcoCoachesFn({ data: { slug: params.slug } }),
    ]);
    if (!parent) throw notFound();
    return { title: `${parent.cco.name} coaches`, cco: parent.cco, coaches };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { slug: string };
  }) => {
    const name = loaderData?.cco.name ?? params.slug;
    return createBrandMeta({
      title: `${name} coaches | Dream Coach Match`,
      description: `All coaches credentialed by ${name}.`,
      ogUrl: `https://dreamcoachmatch.com/organizations/${params.slug}/coaches`,
      canonical: `https://dreamcoachmatch.com/organizations/${params.slug}/coaches`,
      // Empty listing pages ("0 coaches") are thin/orphan content that hurts
      // SEO and misrepresents credential coverage to crawlers.
      noindexFollow: (loaderData?.coaches.length ?? 0) === 0,
    });
  },
  component: CcoCoachesPage,
});

function CcoCoachesPage() {
  const { cco, coaches } = Route.useLoaderData();
  const { slug } = Route.useParams();

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-8 pb-12 md:pt-10 md:pb-16 xl:px-0">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Organizations", href: "/organizations" },
              { label: cco.name, href: `/organizations/${slug}` },
              { label: "Coaches" },
            ]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-purple-700 uppercase">
            ● {cco.name}
          </div>
          <h1 className="mt-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl lg:text-5xl xl:text-6xl">
            {cco.name} coaches
          </h1>
          <div className="mt-6 flex flex-wrap gap-6 text-sm">
            <Link
              to="/organizations/$slug/training-programs"
              params={{ slug }}
              className="font-medium text-purple-700 hover:text-purple-800"
            >
              See training programs →
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-14 md:py-20 xl:px-0">
        <CihCoachCellGrid coaches={coaches} />
      </div>

      <CihClosingCta
        headline={`Find a ${cco.name}-credentialed coach for your situation.`}
        cta={
          <Link to="/discovery" search={{ source: `cco:${slug}:coaches` }}>
            <Button variant="primary" size="lg">
              Start →
            </Button>
          </Link>
        }
      />
    </>
  );
}
